import { useEffect, useRef, useState } from 'react';
import type { ArchitectureHealth } from '@ddp/shared';

import { ApiError, apiFetch } from '../../api/client.js';

/** Cadence de rafraîchissement du schéma d'état (ADR-0037 §5 : interrogation, pas de WebSocket). */
const POLL_INTERVAL_MS = 15_000;

export interface ArchitectureHealthState {
  health: ArchitectureHealth | null;
  loading: boolean;
  /** Code HTTP de la dernière erreur, pour distinguer un 404 (non autorisé / non partagé). */
  errorStatus: number | null;
  error: string | null;
}

/**
 * Charge l'état d'architecture et le rafraîchit périodiquement tant que `enabled`. `path` varie selon
 * l'appelant : la page admin-org lit `/architecture-health`, la console plateforme lit la vue d'une
 * organisation (servie seulement si elle a consenti, 404 sinon — exposé via `errorStatus`).
 *
 * Le premier chargement montre un état de chargement ; les rafraîchissements suivants sont silencieux
 * (on garde l'ancienne valeur à l'écran plutôt que de clignoter).
 */
export function useArchitectureHealth(path: string, enabled = true): ArchitectureHealthState {
  const [state, setState] = useState<ArchitectureHealthState>({
    health: null,
    loading: enabled,
    errorStatus: null,
    error: null,
  });
  // Le premier passage affiche le chargement ; les suivants rafraîchissent sans vider l'écran.
  const firstLoad = useRef(true);

  useEffect(() => {
    if (!enabled) {
      setState({ health: null, loading: false, errorStatus: null, error: null });
      return;
    }
    let active = true;
    firstLoad.current = true;

    const tick = async () => {
      if (firstLoad.current) setState((s) => ({ ...s, loading: true }));
      try {
        const data = await apiFetch<ArchitectureHealth>(path);
        if (!active) return;
        setState({ health: data, loading: false, errorStatus: null, error: null });
      } catch (err) {
        if (!active) return;
        const status = err instanceof ApiError ? err.status : null;
        setState((s) => ({
          // On conserve la dernière mesure connue en cas d'erreur transitoire.
          health: s.health,
          loading: false,
          errorStatus: status,
          error: err instanceof Error ? err.message : 'Chargement impossible.',
        }));
      } finally {
        firstLoad.current = false;
      }
    };

    void tick();
    const timer = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [path, enabled]);

  return state;
}
